import { ChangeDetectionStrategy, Component, DoCheck, ElementRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { RouterModule } from '@angular/router';
import { HideUpComponent } from '@df/ui/hide-up/hide-up.component';
import { ToggleClassDirective } from '@df/ui/toggle/toggle-class.directive';
import { NToggle, ToggleService } from '@df/ui/toggle/toggle.service';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsContent, NICmsContentComponent } from '@icms/core/content';
import { ButtonStyleComponent } from '../../../common/buttons/button-style/button-style.component';
import { NPageOffCanvas, PageOffCanvasComponent } from '../page-off-canvas.component';

let uid = 0;

@Component({
    imports: [ToggleClassDirective, ICmsClientModule, ButtonStyleComponent, RouterModule, HideUpComponent],
    selector: 'page-off-canvas-menu',
    templateUrl: 'page-off-canvas-menu.html',
    styleUrl: 'page-off-canvas-menu.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageOffCanvasMenuComponent extends UiComponent implements NICmsContentComponent.IComponent, DoCheck, OnInit {
    @ICmsContent({
        watchDepth: 1
    })
    @Input()
    component?: NPageOffCanvas.IMenuComponentData & NPageOffCanvas.IMenuItem;
    /**
     * I failed with subcomponents
     */
    protected override canDetach = false;

    /**
     * Access to enum
     */
    readonly EMenuItemType = NPageOffCanvas.EMenuItemType;

    @ViewChild('scrollable', { read: ElementRef })
    public scrollable?: ElementRef<HTMLElement>;

    /**
     * Parent menu (for submenus)
     */
    @Input()
    parent?: NPageOffCanvas.IMenu;

    /**
     * Parents uniq ids
     */
    @Input()
    parentId: string = '0';

    /**
     * Depth level
     */
    @Input()
    level = 0;

    @Input()
    toggleId?: NToggle.TId;

    readonly toggleService = inject(ToggleService);
    private readonly cmsPageOffCanvasMenuComponent = inject(PageOffCanvasComponent);
    uid = `page-off-canvas-menu-${uid++}`;

    /**
     * Getter for only menus with children
     */
    get menusWithChildren() {
        return (
            this.component?.menu?.map((menu, index) => ({ ...menu, index })).filter(i => i.type === NPageOffCanvas.EMenuItemType.menu) || []
        );
    }

    override ngOnInit(): void {
        super.ngOnInit();
        // scroll back to top when opening off canvas (all menus)
        this.subscriptions.add(
            this.cmsPageOffCanvasMenuComponent.offCanvasState.subscribe(open => {
                if (open && this.scrollable) {
                    this.scrollable.nativeElement.scrollTop = 0;
                }
            })
        );
        // scroll back to top when reopening this very menu (submenues)
        if (this.level >= 0) {
            this.subscriptions.add(
                this.cmsPageOffCanvasMenuComponent.level.subscribe(level => {
                    if (level === this.level && this.scrollable) {
                        this.scrollable.nativeElement.scrollTop = 0;
                    }
                    this.detectChanges();
                })
            );
        }
    }

    /**
     * sanitize
     */
    ngDoCheck() {
        this.menusWithChildren.filter(i => !i.subItem).forEach(i => (i.subItem = this.createEmptySubMenu()));
    }

    /**
     * Opens submenu
     */
    menuClick(index: number) {
        this.toggleService.open([this.parentId, index]);
        this.cmsPageOffCanvasMenuComponent.setLevel(this.level + 1);
    }

    getItemData(item: NPageOffCanvas.IMenuComponentData): NICmsContentComponent.IData {
        return Object.assign({}, item, { id: NPageOffCanvas.MENU_COMPONENT_ID });
    }

    /**
     * Force the type (hack to use compoent in cms)
     */
    protected onComponentChange(): void {
        if (this.component) {
            this.component.id = NPageOffCanvas.MENU_COMPONENT_ID;
        }
    }

    // need for remove scroll when per level is bigger
    protected isNotVisible() {
        return this.level !== this.cmsPageOffCanvasMenuComponent.level.value;
    }

    back(): void {
        if (this.toggleId) {
            this.toggleService.close(this.toggleId);
            this.toggleService.closeGroup(this.uid);
        }
        this.cmsPageOffCanvasMenuComponent.back();
    }

    private createEmptySubMenu(): NPageOffCanvas.IMenu {
        const emptyMenu: NPageOffCanvas.IMenu = {
            type: NPageOffCanvas.EMenuItemType.menu,
            text: '',
            link: '',
            menu: [],
            promoTiles: [],
            subItem: undefined as unknown as NPageOffCanvas.IMenu
        };

        emptyMenu.subItem = emptyMenu;

        return emptyMenu;
    }
}
